import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { ProjectConversationId, ProjectId } from 'api/models';
import { ProjectConversation } from 'api/project-conversations';
import { Action, EventType, ObjectType, SocketData } from 'api/pushes';
import { useProjectWSUpdates } from 'hooks/use-project-ws-updates';
import { useUser } from 'hooks/use-user';
import {
  getProjectWideConversationIdQueryKey,
  getProjectWideConversationsQueryKey,
  useProjectConversationsQuery,
} from 'queries/project-wide-conversation';

export const useProjectConversationsListUpdates = (
  projectId: ProjectId,
): {
  conversations: ProjectConversation[];
  isConversationsLoading: boolean;
} => {
  const queryClient = useQueryClient();
  const [, projId] = projectId;
  const { data = [], isLoading: isConversationsLoading } =
    useProjectConversationsQuery(projectId);
  const { user: currentUser } = useUser();

  const updateConversationList = useCallback(
    async (convData: ProjectConversation) => {
      await queryClient.setQueryData<ProjectConversation[]>(
        getProjectWideConversationsQueryKey(projectId),
        (oldConversations = []) => {
          const isAlreadyPresent = oldConversations.find(
            (conv) => conv.conversationId === convData.conversationId,
          );

          const partOfConversation = convData.members?.find(
            ({ user }) => user.userId === currentUser.userId,
          );

          if (isAlreadyPresent) {
            return oldConversations.map((conv) => {
              if (conv.conversationId === convData.conversationId) {
                return {
                  ...convData,
                  partOfConversation: Boolean(partOfConversation),
                };
              }

              return conv;
            });
          }

          return [
            ...oldConversations,
            {
              ...convData,
              partOfConversation: Boolean(partOfConversation),
            },
          ];
        },
      );
    },
    [currentUser.userId, projectId, queryClient],
  );
  const handleSocketCreate = useCallback(
    async (socketData: SocketData<ProjectConversation>) => {
      const { data: convData } = socketData;

      if (socketData.projectId === projId) {
        await updateConversationList(convData);
      }
    },
    [projId, updateConversationList],
  );

  const handleSocketDelete = useCallback(
    async (socketData: SocketData<ProjectConversation>) => {
      const { data: convData } = socketData;

      await queryClient.setQueryData<ProjectConversation[]>(
        getProjectWideConversationsQueryKey(projectId),
        (oldConversations = []) =>
          oldConversations.filter(
            (conv) => conv.conversationId !== convData.conversationId,
          ),
      );
    },
    [projectId, queryClient],
  );

  const handleSocketUpdate = useCallback(
    async (socketData: SocketData<ProjectConversation>) => {
      const { data: convData } = socketData;

      if (socketData.projectId === projId) {
        await queryClient.setQueryData<ProjectConversation>(
          getProjectWideConversationIdQueryKey(
            projectId,
            ProjectConversationId(convData.conversationId),
          ),
          (oldConversation) => {
            return {
              ...oldConversation,
              ...convData,
            };
          },
        );
        // We need to update list also as they're the one which decides whether user is part of
        // conversation or not using `partOfConversation`
        await updateConversationList(convData);
      }
    },
    [projId, projectId, queryClient, updateConversationList],
  );

  useProjectWSUpdates(
    projId,
    handleSocketCreate,
    EventType(Action.Create, ObjectType.ProjectConversation),
  );

  useProjectWSUpdates(
    projId,
    handleSocketDelete,
    EventType(Action.Delete, ObjectType.ProjectConversation),
  );

  useProjectWSUpdates(
    projId,
    handleSocketUpdate,
    EventType(Action.Update, ObjectType.ProjectConversation),
  );

  return {
    conversations: data,
    isConversationsLoading,
  };
};
